import React from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useMetricsUi, headCellSx, textCellSx, MONO_FONT } from "./metricsUi";

const UNAVAILABLE_IDS = [
  "net_sales",
  "discounts",
  "conversion_rate",
  "sell_through",
  "gross_margin_pct",
  "sessions",
  "aur",
  "net_items",
];

/**
 * Compact collapsible list of unavailable metrics + reason codes.
 */
const DataAvailabilitySection = ({ metrics }) => {
  const ui = useMetricsUi();
  const [open, setOpen] = React.useState(false);

  const rows = UNAVAILABLE_IDS.map((id) => metrics?.[id]).filter(
    (m) => m && m.status === "unavailable",
  );

  if (!rows.length) return null;

  return (
    <Box sx={{ ...ui.card, mt: 3, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          cursor: "pointer",
          backgroundColor: ui.surfaceAlt,
          "&:hover": { backgroundColor: ui.colors.hover },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            component="span"
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: ui.warning,
              backgroundColor: ui.warningBg,
              borderRadius: "10px",
              px: 1,
              py: 0.25,
              whiteSpace: "nowrap",
            }}
          >
            {rows.length} UNAVAILABLE
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 13.5, fontWeight: 700, color: ui.colors.textPrimary }}
            >
              Data availability
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: ui.colors.textSecondary }}>
              These metrics need source data that is not connected yet — values
              are never estimated
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" aria-label="Toggle data availability">
          {open ? (
            <ExpandLessIcon sx={{ color: ui.colors.textSecondary }} />
          ) : (
            <ExpandMoreIcon sx={{ color: ui.colors.textSecondary }} />
          )}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx(ui)}>Metric</TableCell>
                <TableCell sx={headCellSx(ui)}>Reason</TableCell>
                <TableCell sx={headCellSx(ui)}>Code</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((metric) => (
                <TableRow
                  key={metric.id}
                  sx={{ "&:last-child td": { borderBottom: "none" } }}
                >
                  <TableCell sx={textCellSx(ui, { fontWeight: 500 })}>
                    {metric.display_name}
                  </TableCell>
                  <TableCell
                    sx={textCellSx(ui, {
                      color: ui.colors.textSecondary,
                      fontSize: 12,
                    })}
                  >
                    {metric.reason || "Required data is not available"}
                  </TableCell>
                  <TableCell
                    sx={textCellSx(ui, {
                      color: ui.colors.textTertiary,
                      fontFamily: MONO_FONT,
                      fontSize: 11,
                    })}
                  >
                    {metric.reason_code || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
};

export default DataAvailabilitySection;
